// src/generators/dzial3_funkcje/temat6_zadania_uzupelniajace_maturalne/podtemat3_otwarte_types/MaturaAxisZeroPointCanonicalOpenTask.js

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


/*
  Zadanie maturalne – otwarte (0–3 pkt)

  Funkcja kwadratowa f ma:
  - daną oś symetrii x = p,
  - jedno miejsce zerowe,
  - punkt należący do wykresu (x0, y0).

  Uczeń ma:
  1) wyznaczyć drugie miejsce zerowe,
  2) obliczyć współczynnik a oraz q (drugą współrzędną wierzchołka),
  3) podać wzór w postaci kanonicznej f(x) = a(x - p)^2 + q.

  Punktacja:
  - 1 pkt: poprawne drugie miejsce zerowe,
  - 1 pkt: poprawne a LUB q (wystarczy jedno z nich),
  - 1 pkt: poprawny wzór w postaci kanonicznej.
*/

/* ========================================================================== */
/*  Pomocnicze: losowanie i parsowanie liczb                                  */
/* ========================================================================== */

function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do {
    v = Math.floor(Math.random() * span) + min;
  } while (exclude.has(v));
  return v;
}

// prosty parser liczby z EquationInput (całkowite, dziesiętne, ułamki P/Q)
function parseNumberLike(raw) {
  if (raw == null) return null;
  let s =
    typeof raw === "object" && raw.raw != null ? String(raw.raw) : String(raw);
  s = s.trim();
  if (!s) return null;

  s = s.replace(/\s+/g, "");
  s = s.replace(/\u2212/g, "-").replace(",", ".");

  // zwykły ułamek P/Q
  const frac = s.match(/^([+\-]?\d+)\s*\/\s*([+\-]?\d+)$/);
  if (frac) {
    const p = parseInt(frac[1], 10);
    const q = parseInt(frac[2], 10);
    if (!q) return null;
    return p / q;
  }

  // całkowita / dziesiętna
  if (/^[+\-]?\d*\.?\d+(?:e[+\-]?\d+)?$/i.test(s)) {
    const v = Number(s);
    return Number.isFinite(v) ? v : null;
  }

  return null;
}

/* ========================================================================== */
/*  Normalizacja RHS postaci kanonicznej + parser a, p, q                     */
/* ========================================================================== */

function getRaw(val) {
  if (typeof val === "string") return val;
  if (val && typeof val === "object") {
    for (const k of ["raw", "value", "text", "input", "expr", "equation", "latex"]) {
      const v = val[k];
      if (typeof v === "string" && v.trim() !== "") return v;
    }
    try {
      const s = String(val);
      if (s && s !== "[object Object]") return s;
    } catch {}
  }
  return "";
}

function latexToAscii(s) {
  return s
    .replace(/\\left\(/g, "(")
    .replace(/\\right\)/g, ")")
    .replace(/\\cdot/g, "")
    .replace(/\^{\s*2\s*}/g, "^2")
    .replace(/\\,/g, "")
    .replace(/\\ /g, "")
    .replace(/\\!/g, "")
    .replace(/\\-/g, "-");
}

// zamiana ułamków mieszanych typu "-2 1/3" → "-7/3"
function normalizeMixedFractions(s) {
  return s.replace(
    /([+\-]?)\s*(\d+)\s+(\d+)\s*\/\s*(\d+)/g,
    (_, sign, whole, num, den) => {
      const W = parseInt(whole, 10);
      const N = parseInt(num, 10);
      const D = parseInt(den, 10);
      if (!D) return _;
      const improperAbs = W * D + N;
      const signChar = sign === "-" ? "-" : sign === "+" ? "+" : "";
      return `${signChar}${improperAbs}/${D}`;
    }
  );
}

function normalizeCanonicalRHS(raw) {
  let s = getRaw(raw);
  if (!s) return "";
  if (s.includes("\\") || /\^{\s*2\s*}/.test(s)) {
    s = latexToAscii(s);
  }
  s = s.replace(/(\d),(\d)/g, "$1.$2"); // 1,25 → 1.25
  s = normalizeMixedFractions(s);
  s = s
    .replace(/\u2212/g, "-")
    .replace(/²/g, "^2")
    .replace(/^f\(x\)=/i, "")
    .replace(/^y=/i, "")
    .replace(/\s+/g, "");
  return s;
}

function parseNumberStrict(s) {
  if (s == null) return null;
  s = s.trim();
  if (!s) return null;
  s = s.replace(/(\d),(\d)/g, "$1.$2");
  if (/^[+\-]?\d*\.?\d+(?:e[+\-]?\d+)?$/i.test(s)) return parseFloat(s);
  const m = s.match(/^([+\-]?\d+)\s*\/\s*([+\-]?\d+)$/);
  if (m) {
    const p = parseInt(m[1], 10);
    const q = parseInt(m[2], 10);
    if (!q) return null;
    return p / q;
  }
  return null;
}

const NUM = "(?:\\d+\\/\\d+|\\d*\\.\\d+|\\d+)";
function parseCanonicalUserRaw(raw) {
  const s = normalizeCanonicalRHS(raw);
  if (!s) return null;
  const re = new RegExp(
    `^([+\\-]?${NUM})?\\(x([+-])(${NUM})\\)\\^2([+-])(${NUM})$`,
    "i",
  );
  const m = s.match(re);
  if (!m) return null;

  // a
  let aStr = m[1];
  let a;
  if (aStr == null || aStr === "") a = 1;
  else if (aStr === "-") a = -1;
  else {
    a = parseNumberStrict(aStr);
    if (a == null) return null;
  }

  // p (uwaga na znak w (x ± something))
  const signP = m[2] === "+" ? +1 : -1;
  const pNum = parseNumberStrict(m[3]);
  if (pNum == null) return null;
  const p = signP === +1 ? -pNum : +pNum;

  // q
  const signQ = m[4] === "+" ? +1 : -1;
  const qNum = parseNumberStrict(m[5]);
  if (qNum == null) return null;
  const q = signQ * qNum;

  return { a, p, q };
}

/* ---------- fallback numeryczny ---------- */

function explicitMultiply(expr) {
  let s = expr;
  s = s.replace(/(\d)(x)/g, "$1*$2");
  s = s.replace(/(\d)\(/g, "$1*(");
  s = s.replace(/\)(\d|x)/g, ")*$1");
  s = s.replace(/x\(/g, "x*(");
  return s;
}
function powToJS(expr) {
  return expr.replace(/\^/g, "**");
}
function safeEvalRHS(rhs, x) {
  let s = normalizeCanonicalRHS(rhs);
  if (!s) return NaN;
  s = explicitMultiply(s);
  s = powToJS(s);
  const js = s.replace(/x/g, `(${x})`);
  try {
    // eslint-disable-next-line no-new-func
    const val = Function(`"use strict"; return (${js});`)();
    return Number.isFinite(val) ? val : NaN;
  } catch {
    return NaN;
  }
}

function numericallyEquivalentToCanonical(userRHS, a, p, q, tol = 1e-3) {
  const xs = [-3, -1, 0, 1, 2, 4];
  for (const x of xs) {
    const u = safeEvalRHS(userRHS, x);
    if (!Number.isFinite(u)) return false;
    const ref = a * (x - p) * (x - p) + q;
    if (Math.abs(u - ref) > tol) return false;
  }
  return true;
}

/* ========================================================================== */
/*  Generator parametrów                                                      */
/* ========================================================================== */

function generateParams() {
  // a – niewielna liczba całkowita różna od 0
  const a = randInt(-4, 4, new Set([0]));

  // p – środek (oś symetrii)
  const p = randInt(-3, 3);

  // r – odległość miejsc zerowych od osi symetrii (całkowita)
  const r = randInt(1, 4);

  const x1 = p + (Math.random() < 0.5 ? r : -r); // podane w treści
  const x2 = 2 * p - x1; // drugie miejsce zerowe

  // q = -a r^2, bo 0 = a(x - p)^2 + q w miejscach zerowych
  const q = -a * r * r;

  // wybierz punkt (x0, y0), który NIE jest miejscem zerowym ani wierzchołkiem
  let x0;
  const forbidden = new Set([x1, x2, p]);
  do {
    const k = randInt(-3, 3, new Set([0]));
    x0 = p + k;
  } while (forbidden.has(x0));
  const y0 = a * (x0 - p) * (x0 - p) + q;

  return { a, p, q, x1, x2, x0, y0 };
}

/* ========================================================================== */
/*  Helper do ładnego zapisu postaci kanonicznej                              */
/* ========================================================================== */

function canonicalBaseLatex(a, p) {
  if (p === 0) {
    return `${a}x^{2}`;
  }
  const sign = p < 0 ? "+" : "-";
  const absP = Math.abs(p);
  return `${a}(x ${sign} ${absP})^{2}`;
}

function canonicalFullLatex(a, p, q) {
  const base = canonicalBaseLatex(a, p);
  if (q === 0) {
    return `f(x) = ${base}`;
  }
  const sign = q < 0 ? "-" : "+";
  const absQ = Math.abs(q);
  return `f(x) = ${base} ${sign} ${absQ}`;
}

/* ========================================================================== */
/*  Public API: generateTask                                                  */
/* ========================================================================== */

function createJune2025OriginalTask() {
  return {
    type: "MaturaAxisZeroPointCanonicalOpenTask",
    data: {
      a: 3,
      p: -1,
      q: -12,
      x1: 1,
      x2: -3,
      x0: 2,
      y0: 15,
      maxPoints: 3,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 13,
    },
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original" && options.session === "2025_CZE") {
    return createJune2025OriginalTask();
  }

  const params = generateParams();
  return {
    type: "MaturaAxisZeroPointCanonicalOpenTask",
    data: {
      ...params,
      maxPoints: 3,
    },
  };
}

/* ========================================================================== */
/*  Treść zadania                                                             */
/* ========================================================================== */

export function renderTask({ data }) {
  const { x0, y0, p, x1 } = data;

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – otwarte (0–3 pkt).</strong>
      </p>

      <p>
        W kartezjańskim układzie współrzędnych{" "}
        <InlineMath math={'(x,y)'} /> wykres funkcji kwadratowej{" "}
        <InlineMath math={'f'} /> przechodzi przez punkt{" "}
        <InlineMath math={`(${x0},\\;${y0})`} />.
      </p>

      <p>
        Osią symetrii tego wykresu jest prosta o równaniu{" "}
        <InlineMath math={`x = ${p}`} />. Jednym z miejsc zerowych funkcji{" "}
        <InlineMath math={'f'} /> jest liczba{" "}
        <InlineMath math={String(x1)} />.
      </p>

      <p>
        Wyznacz wzór funkcji <InlineMath math={'f'} /> w postaci
        kanonicznej. Zapisz obliczenia.
      </p>
    </div>
  );
}

/* ========================================================================== */
/*  Input użytkownika                                                         */
/* ========================================================================== */

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};

  const setField =
    (key) =>
    (val) =>
      onChange({ ...(ua || {}), [key]: val });

  const rawX2 = getRaw(ua.x2);
  const rawA = getRaw(ua.a);
  const rawQ = getRaw(ua.q);
  const rawCanon = getRaw(ua.canonical);

  const Preview = ({ latex }) =>
    latex ? (
      <div style={{ marginTop: 2 }}>
        <strong>Podgląd:</strong> <InlineMath math={latex} />
      </div>
    ) : null;

  return (
    <div style={{ display: "grid", gap: 12, maxWidth: 520 }}>
      <OpenTaskScoringHint variant="independent" />
      {/* x2 */}
      <div style={{ display: "grid", gap: 6 }}>
        <div>
          Drugie miejsce zerowe funkcji <InlineMath math={"f"} />:
        </div>
        <div className="flex items-center gap-2">
          <InlineMath math={"x_2 ="} />
          <EquationInput
            value={ua.x2}
            onChange={setField("x2")}
            disabled={disabled}
            placeholder="np. 3"
            ariaLabel="drugie miejsce zerowe"
            label="x_2"
            showPreview={false} // 🔥 wyłączamy wbudowany "Podgląd: ..."
          />
        </div>
        <Preview latex={rawX2 ? `x_2 = ${rawX2}` : ""} />
      </div>

      {/* a i q */}
      <div style={{ display: "grid", gap: 6 }}>
        <div>
          Oblicz współczynnik <InlineMath math={"a"} /> oraz liczbę{" "}
          <InlineMath math={"q"} />, czyli drugą współrzędną wierzchołka{" "}
          <InlineMath math={"(p,q)"} />:
        </div>

        <div className="flex flex-wrap items-start gap-6">
          <div style={{ display: "grid", gap: 6 }}>
            <div className="flex items-center gap-2">
              <InlineMath math={"a ="} />
              <EquationInput
                value={ua.a}
                onChange={setField("a")}
                disabled={disabled}
                placeholder="np. 4"
                ariaLabel="współczynnik a"
                label="a"
                showPreview={false}
              />
            </div>
            <Preview latex={rawA ? `a = ${rawA}` : ""} />
          </div>

          <div style={{ display: "grid", gap: 6 }}>
            <div className="flex items-center gap-2">
              <InlineMath math={"q ="} />
              <EquationInput
                value={ua.q}
                onChange={setField("q")}
                disabled={disabled}
                placeholder="np. -36"
                ariaLabel="współrzędna q wierzchołka"
                label="q"
                showPreview={false}
              />
            </div>
            <Preview latex={rawQ ? `q = ${rawQ}` : ""} />
          </div>
        </div>
      </div>

      {/* postać kanoniczna */}
      <div style={{ display: "grid", gap: 6 }}>
        <div>
          Zapisz wzór funkcji w postaci kanonicznej{" "}
          <InlineMath math={"f(x)=a(x-p)^2+q"} />. Wpisz prawą stronę:
        </div>
        <div className="flex items-center gap-2">
          <InlineMath math={"f(x) ="} />
          <EquationInput
            value={ua.canonical}
            onChange={setField("canonical")}
            disabled={disabled}
            placeholder="np. 4(x+3)^2-36"
            ariaLabel="postać kanoniczna"
            showPreview={false}
          />
        </div>
        <Preview latex={rawCanon ? `f(x) = ${rawCanon}` : ""} />
      </div>
    </div>
  );
}


/* ========================================================================== */
/*  Podgląd odpowiedzi użytkownika                                            */
/* ========================================================================== */

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const rawX2 =
    typeof userAnswer.x2 === "object"
      ? userAnswer.x2?.raw ?? ""
      : String(userAnswer.x2 ?? "");
  const rawA =
    typeof userAnswer.a === "object"
      ? userAnswer.a?.raw ?? ""
      : String(userAnswer.a ?? "");
  const rawQ =
    typeof userAnswer.q === "object"
      ? userAnswer.q?.raw ?? ""
      : String(userAnswer.q ?? "");
  const rawCanon = getRaw(userAnswer.canonical);

  const x2Latex = rawX2 || "\\text{–}";
  const aLatex = rawA || "\\text{–}";
  const qLatex = rawQ || "\\text{–}";
  const canonLatex = rawCanon || "\\text{–}";

  return (
    <div style={{ display: "grid", gap: 4, textAlign: "left" }}>
      <div>
        <InlineMath math={`x_2 = ${x2Latex}`} />
      </div>
      <div>
        <InlineMath math={`a = ${aLatex}`} />
      </div>
      <div>
        <InlineMath math={`q = ${qLatex}`} />
      </div>
      <div>
        <InlineMath math={`f(x) = ${canonLatex}`} />
      </div>
    </div>
  );
}

/* ========================================================================== */
/*  Sprawdzanie odpowiedzi – 0–3 pkt                                          */
/* ========================================================================== */

export function checkAnswer({ data }, userAnswer) {
  const { x2, a, p, q, maxPoints } = data;
  const ua = userAnswer || {};

  const x2Val = parseNumberLike(ua.x2);
  const aVal = parseNumberLike(ua.a);
  const qVal = parseNumberLike(ua.q);

  const EPS = 1e-2;

  const x2Ok = x2Val != null && Math.abs(x2Val - x2) < EPS;
  const aOk = aVal != null && Math.abs(aVal - a) < EPS;
  const qOk = qVal != null && Math.abs(qVal - q) < EPS;

  let canonicalOk = false;
  if (ua.canonical != null) {
    const parsed = parseCanonicalUserRaw(ua.canonical);
    if (parsed) {
      const rawCanon = getRaw(ua.canonical);
      const usedDecimal = /[.,]\d/.test(rawCanon);
      const EPS_STRICT = 1e-9;
      const EPS_APPROX = 1e-2;
      const tol = usedDecimal ? EPS_APPROX : EPS_STRICT;

      const sameA = Math.abs(parsed.a - a) < EPS_STRICT; // a dokładnie
      const sameP = Math.abs(parsed.p - p) < tol;
      const sameQ = Math.abs(parsed.q - q) < tol;

      canonicalOk = sameA && sameP && sameQ;
    } else {
      // fallback numeryczny (np. przy nietypowym zapisie)
      canonicalOk = numericallyEquivalentToCanonical(
        ua.canonical,
        a,
        p,
        q,
        1e-3,
      );
    }
  }

  let score = 0;

  // 1) punkt za drugie miejsce zerowe
  if (x2Ok) score += 1;

  // 2) punkt za co najmniej jedno z a lub q
  if (aOk || qOk) score += 1;

  // 3) punkt za poprawny wzór kanoniczny
  if (canonicalOk) score += 1;

  if (score > maxPoints) score = maxPoints;

  return {
    isCorrect: score === maxPoints,
    score,
    maxScore: maxPoints,
    parts: {
      secondRoot: x2Ok,
      aOk,
      qOk,
      canonicalOk,
    },
  };
}

/* ========================================================================== */
/*  Krótka poprawna odpowiedź                                                 */
/* ========================================================================== */

export function renderAnswer({ data }) {
  const { a, p, q, x2 } = data;

  return (
    <div style={{ textAlign: "left", display: "grid", gap: 4 }}>
      <div>
        Drugie miejsce zerowe: <InlineMath math={`x_2 = ${x2}`} />
      </div>
      <div>
        Wierzchołek: <InlineMath math={`(p,q) = (${p},\\;${q})`} />
      </div>
      <div>
        Wzór funkcji:{" "}
        <InlineMath math={canonicalFullLatex(a, p, q)} />
      </div>
    </div>
  );
}

/* ========================================================================== */
/*  Wyjaśnienie krok po kroku                                                 */
/* ========================================================================== */

export function renderExplanation({ data }) {
  const { a, p, q, x1, x2, x0, y0 } = data;

  // helper: liczby ujemne w nawiasach, dodatnie bez
  const fmt = (n) => (n < 0 ? `(${n})` : `${n}`);

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Oś symetrii funkcji kwadratowej leży w
        połowie między miejscami zerowymi. Jeśli{" "}
        <InlineMath math={'x_1'} /> i <InlineMath math={'x_2'} /> są
        miejscami zerowymi, to
      </p>
      <BlockMath math={'p = \\dfrac{x_1 + x_2}{2}.'} />
      <p>
        Znamy <InlineMath math={'p'} /> oraz jedno miejsce zerowe{" "}
        <InlineMath math={`x_1 = ${x1}`} />, więc
      </p>
      <BlockMath
        math={`x_2 = 2p - x_1 = 2\\cdot${fmt(p)} - ${fmt(x1)} = ${x2}.`}
      />

      <p>
        <strong>Krok 2.</strong> Skoro znamy oba miejsca zerowe{" "}
        <InlineMath math={'x_1'} /> i <InlineMath math={'x_2'} />, to
        funkcję można zapisać w postaci iloczynowej
      </p>
      <BlockMath
        math={`f(x) = a(x - x_1)(x - x_2).`}
      />
      <p>
        Wiemy, że punkt <InlineMath math={`(${x0},${y0})`} /> należy do
        wykresu, więc po podstawieniu otrzymujemy
      </p>
      <BlockMath
        math={`y_0 = a(x_0 - x_1)(x_0 - x_2).`}
      />
      <BlockMath
        math={`a = \\dfrac{${y0}}{(${fmt(x0)} - ${fmt(
          x1,
        )})(${fmt(x0)} - ${fmt(x2)})} = ${a}.`}
      />

      <p>
        <strong>Krok 3.</strong> Współrzędna{" "}
        <InlineMath math={'q'} /> jest wartością funkcji w wierzchołku,
        czyli <InlineMath math={'q = f(p)'} />. Korzystamy z postaci
        iloczynowej:
      </p>
      <BlockMath
        math={`q = f(p) = a(p - x_1)(p - x_2).`}
      />
      <BlockMath
        math={`q = ${a}(${fmt(p)} - ${fmt(x1)})(${fmt(p)} - ${fmt(
          x2,
        )}) = ${q}.`}
      />

      <p>
        <strong>Krok 4.</strong> Znamy już{" "}
        <InlineMath math={'a'} />, <InlineMath math={'p'} /> i{" "}
        <InlineMath math={'q'} />, więc zapisujemy wzór funkcji w postaci
        kanonicznej:
      </p>
      <BlockMath math={canonicalFullLatex(a, p, q)} />

      <p>
        Uwaga: można też najpierw rozwinąć postać iloczynową do postaci
        ogólnej i obliczyć <InlineMath math={'q'} /> ze wzoru{" "}
        <InlineMath math={'q = -\\dfrac{\\Delta}{4a}'} />, ale metoda z{" "}
        <InlineMath math={'q = f(p)'} /> jest zwykle szybsza i prostsza
        rachunkowo.
      </p>
    </div>
  );
}

